/*
 * Stufen-Leiste in der Announcement-Bar (Spec docs/2026-10-12-stufen-leiste-design.md,
 * abgenommenes Mockup Stand 5: docs/2026-10-12-stufen-leiste-mockup-stand5.html).
 *
 * Teil 1, reine Funktionen ohne DOM (Lage, Texte, Verlaufs-Stufe, neue Stufe).
 * Laeuft in Node (module.exports, tests/stufen-leiste.test.cjs) und im Browser
 * als window.MoonouStufenLeiste. Liquid-Gegenstueck fuer den ersten Stand:
 * snippets/moonou-stufen-leiste.liquid. Wer eine Regel hier aendert, aendert
 * sie dort genauso.
 *
 * Teil 2, nur Browser: Custom Element <moonou-stufen-leiste>. Nach jeder
 * Warenkorb-Aenderung (theme.pubsub cartUpdate, Event cart:refresh) holt es die
 * eigene Section neu (Section Rendering API, ?sections=<id>), liest Lampenzahl und Stufen aus den Daten-Attributen und
 * aendert die BESTEHENDE Leiste (nie das Element ersetzen, sonst gleitet der
 * Verlauf nicht).
 *
 * Der Browser-Name MoonouStufenLeiste darf nie als DOM-id vorkommen.
 */
(function (root) {
  'use strict';

  const MAX_PROZENT = 90;

  function ganz(value) {
    const text = String(value == null ? '' : value).trim();
    if (!/^-?\d+$/.test(text)) return NaN;
    return Number(text);
  }

  /* "2:10,3:15" -> [{min: 2, prozent: 10}, {min: 3, prozent: 15}].
     Gleiche Saeuberung wie normalizeTiers (assets/moonou-angebot.js): ganze
     Zahlen, min >= 1, prozent 1 bis 90, je min nur der erste Eintrag,
     aufsteigend. Kaputtes faellt weg, nie „NaN %". */
  function parseStufen(text) {
    if (typeof text !== 'string') return [];
    const gesehen = new Set();
    const out = [];
    for (const teil of text.split(',')) {
      const [a, b, ...rest] = teil.split(':');
      if (rest.length) continue;
      const min = ganz(a);
      const prozent = ganz(b);
      if (!(min >= 1) || !(prozent >= 1) || prozent > MAX_PROZENT) continue;
      if (gesehen.has(min)) continue;
      gesehen.add(min);
      out.push({ min, prozent });
    }
    return out.sort((x, y) => x.min - y.min);
  }

  function anzahlSauber(n) {
    const zahl = Number(n);
    return Number.isFinite(zahl) && zahl > 0 ? Math.floor(zahl) : 0;
  }

  /* Erreichte und naechste Stufe, fehlende Lampen bis zur naechsten. */
  function lage(stufen, n) {
    const anzahl = anzahlSauber(n);
    const liste = Array.isArray(stufen) ? stufen : [];
    const erreicht = liste.filter((s) => anzahl >= s.min).pop() || null;
    const naechste = liste.find((s) => anzahl < s.min) || null;
    return { erreicht, naechste, fehlt: naechste ? naechste.min - anzahl : 0 };
  }

  function lampen(n) {
    return n + ' ' + (n === 1 ? 'Lampe' : 'Lampen');
  }

  /* Handy, immer: den Stand zeigen die Pillen. */
  function textHandy(stufen) {
    if (!Array.isArray(stufen) || !stufen.length) return '';
    return 'Mengenrabatt ab ' + lampen(stufen[0].min);
  }

  function textDesktop(stufen, n) {
    if (!Array.isArray(stufen) || !stufen.length) return '';
    const anzahl = anzahlSauber(n);
    const { erreicht, naechste, fehlt } = lage(stufen, anzahl);
    if (anzahl === 0) return textHandy(stufen);
    if (!erreicht) return 'Noch ' + lampen(fehlt) + ' bis ' + naechste.prozent + ' % Rabatt';
    if (!naechste) return erreicht.prozent + ' % Rabatt aktiv';
    return erreicht.prozent + ' % Rabatt aktiv · noch ' + lampen(fehlt) + ' bis ' + naechste.prozent + ' %';
  }

  function textErfolg(stufe) {
    return stufe ? stufe.prozent + ' % Rabatt freigeschaltet' : '';
  }

  /* Verlauf 0 bis 3 (data-stufe). Bei Stufen 2/3 genau min(Lampen, 3).
     Allgemein: skaliert ueber die hoechste Mindestzahl, 3 erst mit der
     hoechsten Stufe, jede Lampe davor mindestens 1 und hoechstens 2. */
  function stufenLevel(stufen, n) {
    if (!Array.isArray(stufen) || !stufen.length) return 0;
    const anzahl = anzahlSauber(n);
    const hoechste = stufen[stufen.length - 1].min;
    if (anzahl <= 0) return 0;
    if (anzahl >= hoechste) return 3;
    return Math.min(2, Math.max(1, Math.round((anzahl * 3) / hoechste)));
  }

  /* Neue Stufe erreicht: nur wenn die Lampenzahl gestiegen ist und die
     erreichte Stufe danach eine andere ist als davor. */
  function neueStufe(stufen, alt, neu) {
    const vorher = anzahlSauber(alt);
    const nachher = anzahlSauber(neu);
    if (!(nachher > vorher)) return null;
    const davor = lage(stufen, vorher).erreicht;
    const jetzt = lage(stufen, nachher).erreicht;
    if (!jetzt) return null;
    if (davor && davor.min === jetzt.min) return null;
    return jetzt;
  }

  /* Pillen-Modell: Zustand je Stufe und Strich davor. */
  function pillen(stufen, n) {
    if (!Array.isArray(stufen)) return [];
    const anzahl = anzahlSauber(n);
    const { naechste } = lage(stufen, anzahl);
    return stufen.map((s, i) => ({
      min: s.min,
      prozent: s.prozent,
      zustand: anzahl >= s.min ? 'erreicht' : naechste && s.min === naechste.min ? 'naechste' : '',
      strich: i > 0,
      strichVoll: i > 0 && anzahl >= s.min
    }));
  }

  function pillenHtml(stufen, n) {
    return pillen(stufen, n)
      .map((p) => {
        const strich = p.strich ? '<span class="msl__strich' + (p.strichVoll ? ' is-voll' : '') + '"></span>' : '';
        const cls = 'msl__pille' + (p.zustand ? ' is-' + p.zustand : '');
        return strich + '<span class="' + cls + '" data-stufe-min="' + p.min + '"><span class="msl__kreis">' + p.min + '</span>' + p.prozent + ' %</span>';
      })
      .join('');
  }

  /* Uhr der Leiste wie im Mockup: "2 T 14:32:09", unter 1 Tag "14:32:09",
     zweistellig. Nur fuer die Leiste, die Theme-Uhr (CountdownTimer) bleibt
     an allen anderen Stellen wie sie ist. */
  function uhrText(sekunden) {
    const n = Number(sekunden);
    let s = Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
    const tage = Math.floor(s / 86400);
    s -= tage * 86400;
    const z = (v) => String(v).padStart(2, '0');
    const zeit = z(Math.floor(s / 3600)) + ':' + z(Math.floor((s % 3600) / 60)) + ':' + z(s % 60);
    return tage > 0 ? tage + ' T ' + zeit : zeit;
  }

  const api = { uhrText, parseStufen, lage, lampen, textHandy, textDesktop, textErfolg, stufenLevel, neueStufe, pillen, pillenHtml };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
    return;
  }
  root.MoonouStufenLeiste = api;

  /* ---------- Teil 2: Custom Element ---------- */
  if (!root.customElements || root.customElements.get('moonou-stufen-leiste')) return;

  const doc = root.document;
  const HAK = '<svg class="msl__hak" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
  const FUNKEN_FARBEN = ['#FFFFFF', '#FFD9A0', '#F4DBC9', '#FFE9C2'];

  function ruhig() {
    return root.matchMedia ? root.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  }

  function drawerOffen() {
    const drawer = doc.querySelector('cart-drawer');
    return !!drawer && (drawer.open === true || drawer.hasAttribute('open'));
  }

  function hoeheSetzen(px) {
    doc.documentElement.style.setProperty('--moonou-leiste-hoehe', px);
  }

  function wegSetzen(px) {
    doc.documentElement.style.setProperty('--moonou-leiste-weg', px);
  }

  class StufenLeiste extends HTMLElement {
    connectedCallback() {
      this.__zaehler = 0;
      this.__basis = null;
      this.__textTimer = 0;
      this.__neuTimer = 0;
      this.stufen = parseStufen(this.getAttribute('data-stufen') || '');
      this.anzahl = anzahlSauber(this.getAttribute('data-anzahl'));

      this.__onAenderung = () => this.aktualisieren();
      this.__onDrawerZu = () => this.drawerZu();

      doc.addEventListener('cart:refresh', this.__onAenderung);
      this.pubsubAbonnieren();
      if (!this.__pubsubAus) {
        /* theme.js noch nicht bereit: einmal nach dem Theme-Start versuchen. */
        this.__onThemeBereit = () => this.pubsubAbonnieren();
        doc.addEventListener('page:loaded', this.__onThemeBereit, { once: true });
        doc.addEventListener('DOMContentLoaded', this.__onThemeBereit, { once: true });
      }

      /* Kein Scroll-Listener: das Wegrollen der Anlass-Zeile macht CSS
         (negatives top der Section). Gemessen wird nur bei Groessenwechsel. */
      if (root.ResizeObserver) {
        this.__ro = new ResizeObserver(() => this.hoeheMessen());
        this.__ro.observe(this);
        const anlass = this.querySelector('.msl__anlass');
        if (anlass) this.__ro.observe(anlass);
      }
      this.hoeheMessen();

      /* Gleicher Stand wie aus Liquid, nur aus derselben Logik wie spaeter. */
      this.zustandSetzen();

      /* Eigene Uhr-Anzeige: Zielzeit ist data-expires der versteckten
         Theme-Uhr, die moonou-sale-countdown.js setzt und nach jedem Ende neu
         stellt. Ablauf/Ausblenden bleibt dort. */
      this.uhrTick();
      this.__uhrTimer = setInterval(() => this.uhrTick(), 1000);
    }

    uhrTick() {
      const anzeige = this.querySelector('[data-msl-zeit]');
      const timer = this.querySelector('.msl__uhr countdown-timer');
      if (!anzeige || !timer) return;
      const ziel = new Date(timer.getAttribute('data-expires') || '').getTime();
      if (!Number.isFinite(ziel)) return;
      const text = uhrText((ziel - Date.now()) / 1000);
      if (anzeige.textContent !== text) anzeige.textContent = text;
    }

    disconnectedCallback() {
      doc.removeEventListener('cart:refresh', this.__onAenderung);
      doc.removeEventListener('drawer:afterHide', this.__onDrawerZu);
      if (this.__onThemeBereit) {
        doc.removeEventListener('page:loaded', this.__onThemeBereit);
        doc.removeEventListener('DOMContentLoaded', this.__onThemeBereit);
      }
      if (this.__pubsubAus) this.__pubsubAus();
      this.__pubsubAus = null;
      if (this.__ro) this.__ro.disconnect();
      clearTimeout(this.__textTimer);
      clearTimeout(this.__neuTimer);
      clearInterval(this.__uhrTimer);
      hoeheSetzen('0px');
      wegSetzen('0px');
    }

    pubsubAbonnieren() {
      if (this.__pubsubAus) return;
      const t = root.theme;
      if (!t || !t.pubsub || !t.pubsub.PUB_SUB_EVENTS || typeof t.pubsub.subscribe !== 'function') return;
      this.__pubsubAus = t.pubsub.subscribe(t.pubsub.PUB_SUB_EVENTS.cartUpdate, this.__onAenderung);
    }

    /* weg = Hoehe der Anlass-Zeile, die beim Scrollen oben verschwindet
       (nur Handy/Tablet, auf dem Desktop steht sie in derselben Zeile).
       hoehe = was im geklebten Zustand sichtbar bleibt. */
    hoeheMessen() {
      if (!this.isConnected) return;
      const gesamt = this.getBoundingClientRect().height;
      const anlass = this.querySelector('.msl__anlass');
      const desktop = root.matchMedia && root.matchMedia('(min-width: 1024px)').matches;
      const weg = !desktop && anlass ? anlass.getBoundingClientRect().height : 0;
      wegSetzen(weg.toFixed(1) + 'px');
      hoeheSetzen(Math.max(0, gesamt - weg).toFixed(1) + 'px');
    }

    /* Gemessen 12.10.: Bewusst der aktuelle Pfad statt theme.routes.root_url,
       auf der Startseite rendert die Announcement-Bar wegen hide_homepage
       nichts. Und ?sections= (JSON) statt ?section_id=: der lokale Dev-Server
       beantwortet section_id fuer Header-Group-Sections nach dem ersten
       Browser-Besuch mit 404, sections= (wie im Warenkorb) laeuft stabil. */
    abschnittUrl(id) {
      return root.location.pathname + '?sections=' + encodeURIComponent(id);
    }

    aktualisieren() {
      const id = this.getAttribute('data-section-id');
      if (!id) return;
      const nr = ++this.__zaehler;
      fetch(this.abschnittUrl(id), { cache: 'no-store', credentials: 'same-origin' })
        .then((antwort) => (antwort.ok ? antwort.json() : Promise.reject(new Error('HTTP ' + antwort.status))))
        .then((daten) => {
          if (nr !== this.__zaehler || !this.isConnected) return;
          const html = daten && typeof daten[id] === 'string' ? daten[id] : '';
          if (!html) return;
          const neu = new DOMParser().parseFromString(html, 'text/html').querySelector('moonou-stufen-leiste');
          if (!neu) return;
          this.anwenden(neu.getAttribute('data-anzahl'), neu.getAttribute('data-stufen'));
        })
        .catch((fehler) => {
          if (root.console) root.console.warn('[stufen-leiste] Aktualisierung fehlgeschlagen', fehler);
        });
    }

    anwenden(anzahlText, stufenText) {
      const alt = this.anzahl;
      const stufen = parseStufen(stufenText || '');
      if (stufen.length) this.stufen = stufen;
      this.anzahl = anzahlSauber(anzahlText);
      this.zustandSetzen();

      if (!(this.anzahl > alt) || ruhig()) return;
      if (drawerOffen()) {
        if (this.__basis === null) this.__basis = alt;
        doc.removeEventListener('drawer:afterHide', this.__onDrawerZu);
        doc.addEventListener('drawer:afterHide', this.__onDrawerZu);
        return;
      }
      const basis = this.__basis === null ? alt : Math.min(this.__basis, alt);
      this.__basis = null;
      this.animieren(basis, this.anzahl);
    }

    drawerZu() {
      if (drawerOffen()) return;
      doc.removeEventListener('drawer:afterHide', this.__onDrawerZu);
      const basis = this.__basis;
      this.__basis = null;
      if (basis === null || !(this.anzahl > basis) || ruhig()) return;
      this.animieren(basis, this.anzahl);
    }

    /* Zustand ohne Animation: Verlauf (gleitet per CSS), Pillen, Texte. */
    zustandSetzen() {
      this.setAttribute('data-anzahl', String(this.anzahl));
      this.setAttribute('data-stufe', String(stufenLevel(this.stufen, this.anzahl)));
      const pillenEl = this.querySelector('[data-pillen]');
      if (pillenEl) pillenEl.innerHTML = pillenHtml(this.stufen, this.anzahl);
      this.texte(null);
    }

    texte(erfolgStufe) {
      const el = this.querySelector('[data-text]');
      if (!el) return;
      clearTimeout(this.__textTimer);
      if (erfolgStufe && !ruhig()) {
        el.classList.remove('is-erfolg');
        el.innerHTML = HAK + textErfolg(erfolgStufe);
        void el.offsetWidth;
        el.classList.add('is-erfolg');
        this.__textTimer = setTimeout(() => this.texte(null), 2000);
        return;
      }
      el.classList.remove('is-erfolg');
      const handy = doc.createElement('span');
      handy.className = 'msl__text-handy';
      handy.textContent = textHandy(this.stufen);
      const desktop = doc.createElement('span');
      desktop.className = 'msl__text-desktop';
      desktop.textContent = textDesktop(this.stufen, this.anzahl);
      el.replaceChildren(handy, desktop);
    }

    /* Lichtstreif bei jeder neuen Lampe, Pop + Funken + Erfolgstext bei neuer Stufe. */
    animieren(alt, neu) {
      this.glanz();
      const stufe = neueStufe(this.stufen, alt, neu);
      if (!stufe) return;
      const pille = this.querySelector('[data-stufe-min="' + stufe.min + '"]');
      if (pille) {
        pille.classList.remove('is-neu');
        void pille.offsetWidth;
        pille.classList.add('is-neu');
        clearTimeout(this.__neuTimer);
        this.__neuTimer = setTimeout(() => pille.classList.remove('is-neu'), 1000);
      }
      root.requestAnimationFrame(() => this.funken(stufe.min));
      this.texte(stufe);
    }

    glanz() {
      const el = this.querySelector('.msl__glanz');
      if (!el || typeof el.animate !== 'function') return;
      el.animate(
        [{ transform: 'translateX(-60%)', opacity: 0 }, { opacity: 1, offset: 0.25 }, { transform: 'translateX(160%)', opacity: 0 }],
        { duration: 900, easing: 'cubic-bezier(.3,.7,.4,1)' }
      );
    }

    funken(min) {
      const pille = this.querySelector('[data-stufe-min="' + min + '"]');
      if (!pille) return;
      const a = this.getBoundingClientRect();
      const p = pille.getBoundingClientRect();
      const x = p.left - a.left + p.width / 2;
      const y = p.top - a.top + p.height / 2;
      for (let i = 0; i < 16; i++) {
        const f = doc.createElement('span');
        f.className = 'msl__funke';
        f.setAttribute('aria-hidden', 'true');
        f.style.left = x + 'px';
        f.style.top = y + 'px';
        f.style.background = FUNKEN_FARBEN[i % FUNKEN_FARBEN.length];
        this.appendChild(f);
        if (typeof f.animate !== 'function') {
          f.remove();
          continue;
        }
        const w = (i / 16) * Math.PI * 2 + Math.random() * 0.4;
        const r = 22 + Math.random() * 26;
        f.animate(
          [
            { transform: 'translate(-50%,-50%) scale(1)', opacity: 1 },
            { transform: 'translate(calc(-50% + ' + Math.cos(w) * r + 'px), calc(-50% + ' + Math.sin(w) * r * 0.7 + 'px)) scale(.2)', opacity: 0 }
          ],
          { duration: 650 + Math.random() * 250, easing: 'cubic-bezier(.2,.8,.3,1)' }
        ).onfinish = () => f.remove();
      }
    }
  }

  root.customElements.define('moonou-stufen-leiste', StufenLeiste);
})(typeof window !== 'undefined' ? window : globalThis);
